import { surahMeta } from '../quran/surahMeta';
import { arabicDay } from '../format/arabicDate';

/**
 * The certificate itself — what the جمعية says, and how it is printed.
 *
 * "تشهد جمعية فصّلت بأن فلانة أتمّت تلاوة سورة الإخلاص بتقدير ممتاز."
 *
 * Built as HTML and handed to the platform's own printer, not drawn with a PDF
 * library. That is a deliberate choice about ARABIC: shaping and bidi are the
 * hard part of an Arabic PDF, a mis-shaped certificate is worse than none, and
 * the operating system already does both correctly for the text it renders.
 * iOS and Android print HTML to PDF; a browser prints the same page. The
 * wording, the layout and the fonts are therefore one file, shared by both
 * clients, rather than two drawings that can drift apart.
 */

export type Grade = 'excellent' | 'veryGood' | 'good' | 'passed';

export type Certificate = {
  id: string;
  studentId: string;
  studentName: string;
  surah: number;
  ayahs: number;
  errors: number;
  grade: Grade;
  issuedAt: number;
};

const GRADES: Grade[] = ['excellent', 'veryGood', 'good', 'passed'];

export function isGrade(value: unknown): value is Grade {
  return typeof value === 'string' && GRADES.includes(value as Grade);
}

/**
 * The verb, agreeing with the holder.
 *
 * أتمّ for a boy and أتمّت for a girl. A certificate that addresses half the
 * children it is given to in the wrong gender is not a small blemish in
 * Arabic — it reads as a form nobody checked.
 */
export function completedVerb(gender: 'male' | 'female' | null | undefined): string {
  return gender === 'female' ? 'أتمّت' : 'أتمّ';
}

/** The sentence the certificate exists to say. */
export function attestation(input: {
  studentName: string;
  surahName: string;
  gradeLabel: string;
  gender?: 'male' | 'female' | null;
}): string {
  const { studentName, surahName, gradeLabel } = input;
  return `تشهد جمعية فصّلت بأن ${studentName} ${completedVerb(input.gender)}`
    + ` تلاوة ${surahName} بتقدير ${gradeLabel}`;
}

/** Western digits in every language — the house rule for numbers. */
const digits = (n: number): string => new Intl.NumberFormat('en-US').format(n);

/**
 * The issue date, as a plain Gregorian day in Western digits.
 *
 * Built here rather than by Intl. ar-SA-u-ca-gregory-nu-latn is honoured by
 * Node's full ICU and ignored by iOS, which renders ٥ سبتمبر، ٢٠٢٦ — so a
 * green test suite said nothing about what the device printed. Twelve month
 * names cost less than a rule the runtime is free to disregard.
 */
export function issuedOn(at: number): string {
  return arabicDay(new Date(Number.isFinite(at) && at > 0 ? at : Date.now()));
}

export type CertificateStrings = {
  /** The heading — the جمعية's own name as it signs. */
  issuer: string;
  title: string;
  gradeLabel: string;
  surahName: string;
  ayahsLabel: string;
  issuedLabel: string;
  serialLabel: string;
};

/**
 * One page of HTML, printable to PDF by the platform.
 *
 * Self-contained: no network font, no image, no script. A certificate that
 * needs the internet to render is one a family cannot open again in a year.
 */
export function certificateHtml(
  certificate: Certificate,
  strings: CertificateStrings,
  gender?: 'male' | 'female' | null,
): string {
  const sentence = attestation({
    studentName: certificate.studentName || '—',
    surahName: strings.surahName,
    gradeLabel: strings.gradeLabel,
    gender,
  });

  return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(strings.title)}</title>
<style>
  @page { size: A4 landscape; margin: 0; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: "IBM Plex Sans Arabic", "Geeza Pro", "Noto Naskh Arabic", serif;
    color: #00434F;
    background: #FFFFFF;
  }
  .sheet {
    width: 100%;
    min-height: 100vh;
    padding: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .frame {
    width: 100%;
    height: 100%;
    border: 3px solid #005363;
    border-radius: 18px;
    padding: 40px 56px;
    text-align: center;
  }
  .issuer { font-size: 20px; letter-spacing: 1px; color: #005363; }
  .title { font-size: 34px; font-weight: 700; margin: 18px 0 28px; }
  .sentence { font-size: 26px; line-height: 2; margin: 0 auto; max-width: 40ch; }
  .grade { font-size: 30px; font-weight: 700; color: #005363; margin-top: 26px; }
  .meta { margin-top: 34px; font-size: 15px; color: #55707A; }
  .meta span { margin: 0 14px; }
  .serial { margin-top: 10px; font-size: 12px; color: #8E8E93; direction: ltr; }
</style>
</head>
<body>
  <div class="sheet">
    <div class="frame">
      <div class="issuer">${escapeHtml(strings.issuer)}</div>
      <div class="title">${escapeHtml(strings.title)}</div>
      <p class="sentence">${escapeHtml(sentence)}</p>
      <div class="grade">${escapeHtml(strings.gradeLabel)}</div>
      <div class="meta">
        <span>${escapeHtml(strings.ayahsLabel)}: ${digits(certificate.ayahs)}</span>
        <span>${escapeHtml(strings.issuedLabel)}: ${escapeHtml(issuedOn(certificate.issuedAt))}</span>
      </div>
      <div class="serial">${escapeHtml(strings.serialLabel)}: ${escapeHtml(certificate.id)}</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Escaped, because a display name is somebody's own text.
 *
 * A child called "<script>" is not an attack anybody planned, but their name
 * still must not become markup in a document their family keeps.
 */
export function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** The surah's own name, for the sentence. */
export function surahTitle(surah: number, language: string): string {
  const meta = surahMeta(surah);
  if (!meta) return String(surah);
  return language === 'en' ? meta.englishName : meta.name;
}
